import React from 'react';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import { defaultMegaMenuData } from '../Layout/MegaMenu';

export interface CategoriesPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (payload: { category: string; label: string; path: string }) => void;
}

const CategoriesPickerDialog: React.FC<CategoriesPickerDialogProps> = ({ open, onClose, onSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handlePick = (category: string, label: string) => {
    const path = `/all-products?category=${encodeURIComponent(category)}&sub=${encodeURIComponent(label)}`;
    onSelect({ category, label, path });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          width: isMobile ? '100vw' : { xs: '92vw', sm: 680, md: 860 },
          mx: 'auto',
          maxHeight: isMobile ? '100vh' : '80vh',
          overflow: 'hidden',
        },
      }}
    >
      <AppBar sx={{ position: 'sticky', boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
        <Toolbar sx={{ minHeight: 48 }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Kategorien wählen
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 1, overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 48px)' : 'calc(80vh - 48px)' }}>
        {Object.keys(defaultMegaMenuData)
          .filter((k) => k.toLowerCase() !== 'default')
          .map((cat) => {
            const catData: any = (defaultMegaMenuData as any)[cat];
            if (!catData) return null;
            return (
              <Accordion key={cat} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75 }}>
                  <Typography sx={{ fontWeight: 700 }}>{cat}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
                  {(catData.rightByKey?.all || catData.right || []).map((col: any) => (
                    <Box key={`${cat}-${col.title}`} sx={{ mb: 1.25 }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>
                        {col.title}
                      </Typography>
                      <List disablePadding>
                        {(col.items || []).map((sub: any) => {
                          const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                          return (
                            <ListItemButton
                              key={`${cat}-${col.title}-${label}`}
                              sx={{ borderRadius: 1, py: 0.75 }}
                              onClick={() => handlePick(cat, label)}
                            >
                              <ListItemText primary={label} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Box>
    </Dialog>
  );
};

export default CategoriesPickerDialog;
